import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { Badge, Cascader, Col, Divider, Flex, Row, Tooltip } from 'antd';
import { faMicrosoft } from '@fortawesome/free-brands-svg-icons';
import { faExternalLink, faFilterList } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DefaultOptionType } from 'antd/lib/cascader';
import { useParams } from 'react-router-dom';
import { createGlobalStyle, css } from 'styled-components';

import { trackEvent } from 'jf/analytics/Analytics';
import { MSTeamsUser, RosterClient, TargetRespondent } from 'jf/api';
import { useDevExTheme } from 'jf/common/themes/DevExTheme';
import { DevExButton } from 'jf/components/DevExButton';
import { DevExDrawer } from 'jf/components/DevExDrawer';
import { DevExLink } from 'jf/components/DevExLink';
import { DevExSelect } from 'jf/components/DevExSelect';
import { DevExText } from 'jf/components/DevExText';
import { getDevExEnv } from 'jf/utils/getDevExEnv';
import { useClientQuery } from 'jf/utils/useClientQuery';

import { ClassificationMap } from '../RosterTargetDrawer';

import { MSTeamsTargetList } from './MSTeamsTargetList';

type MSTeamsRosterTargetDrawerProps = {
  open: boolean;
  onClose: () => void;
  addUsers: (users: TargetRespondent[]) => void;
  msteamsUsers: MSTeamsUser[];
};

type Filter = {
  options: DefaultOptionType[];
  value: string;
  type: 'categorical' | 'date' | 'numeric' | 'string';
  label: string;
};

const RosterTargetSegmentCascaderStyles = createGlobalStyle`
.roster-target-segment-cascader {
  .ant-cascader-menu {
    height: auto;
  }
}`;

const RosterTargetDrawerStyles = css`
  &.roster__filter {
    .filter__content {
      padding: ${({ theme }) => theme.variable.spacing.md};
      border: 1px solid ${({ theme }) => theme.color.border.secondary};
      border-radius: ${({ theme }) => theme.variable.borderRadius};
      align-items: center;
      gap: ${({ theme }) => theme.variable.spacing.xs};
    }
    .filter__submit {
      justify-content: flex-end;
    }
    .filter__results {
      justify-content: space-between;
      align-items: center;
    }
    .filter__empty {
      border: 1px solid ${({ theme }) => theme.color.border.secondary};
      border-radius: ${({ theme }) => theme.variable.borderRadius};
      padding: ${({ theme }) => theme.variable.spacing.lg};
      text-align: center;
    }
  }
`;

export const MSTeamsRosterTargetDrawer: React.FC<MSTeamsRosterTargetDrawerProps> = (props) => {
  const theme = useDevExTheme();

  const { studyRef } = useParams<{ studyRef: string }>();

  const { data: rosterFields } = useClientQuery(RosterClient.getRosterSegments, null);
  const [enabledFilters, setEnabledFilters] = useState<Filter[]>([]);
  const [activeFilters, setActiveFilters] = useState<{ [key: string]: string[] }>({});
  const [usersToAdd, setUsersToAdd] = useState<TargetRespondent[]>([]);
  const [usersWithoutMsteams, setUsersWithoutMsteams] = useState<TargetRespondent[]>([]);
  const [rosterFilters, setRosterFilters] = useState<{ [key: string]: Filter }>({});
  const [rosterSegmentOptions, setRosterSegmentOptions] = useState<
    { value: string; label: string }[]
  >([]);
  const defaultKey = 'classification';
  const usersToAddMsteams: MSTeamsUser[] = useMemo(() => {
    const newUsers: (MSTeamsUser | null)[] = usersToAdd.map((u) => u.msteams);
    // linter is not complaining about this in vscode but
    // but the compiler complains, so casting for now.
    const temp: MSTeamsUser[] = newUsers.filter((u) => !!u) as MSTeamsUser[];
    return temp;
  }, [usersToAdd]);

  useEffect(() => {
    if (!rosterFields) {
      return;
    }
    // filters represents the possible values of each individual filter dropdown
    // where key = filter category and filter.options are the possible values
    // in the individual selects
    const filters = rosterFields.segmentValues.reduce(
      (filters, segment) => {
        const keyify = (s) => {
          return s.split(' ').join('-').toLowerCase();
        };
        // matching EMP behavior, which renames role to Job Title
        const segmentName = segment.name === 'Role' ? 'Job Title' : segment.name;
        const segmentNameKey = keyify(segmentName);
        const segmentValue = segment.value;
        if (filters[segmentNameKey]) {
          filters[segmentNameKey].options.push({
            value: segmentValue,
            label:
              segmentNameKey === 'classification'
                ? ClassificationMap[keyify(segmentValue)]
                : segmentValue,
          });
        } else {
          filters[segmentNameKey] = {
            label: segmentName,
            value: segmentNameKey,
            type: 'categorical',
            options: [
              {
                value: segmentValue,
                label:
                  segmentNameKey === 'classification'
                    ? ClassificationMap[keyify(segmentValue)]
                    : segmentValue,
              },
            ],
          };
        }
        return filters;
      },
      {} as { [key: string]: Filter }
    );
    Object.keys(filters).forEach((fkey) => {
      filters[fkey].options = filters[fkey].options.sort((a, b) => {
        const labelA = String(a.label);
        const labelB = String(b.label);
        // typing thinks these are numbers or strings so casting to string
        // these should always be defined and always be strings
        // since they're segmentation values
        return labelA.localeCompare(labelB);
      });
    });
    // segments is passed to the checkbox dropdown
    const segments = Object.keys(filters)
      .sort()
      .map((f) => ({
        value: f,
        label: filters[f].label,
      }));

    // if classification exists (which we believe it should) we want to set that filter by default
    // This wont affect the query, but it implies the functionality of the drawer without
    // user input, allowing for (we hope) better UX
    if (Object.keys(filters).find((f) => f === defaultKey) && !activeFilters.classification) {
      if (!enabledFilters.find((f) => f.value === defaultKey)) {
        setEnabledFilters([...enabledFilters, filters.classification]);
      }
    }

    setRosterFilters(filters);
    setRosterSegmentOptions(segments);
  }, [rosterFields]);

  // gets added to RosterClient.getRosterTargets
  const activeFiltersQueries = useMemo(() => {
    const djangoKey = (filter: Filter, option: string): string => {
      const _option = filter.options.find((o) => o.value === option);
      // recast label back to 'Role' for the query
      const filterLabel = filter.label === 'Job Title' ? 'Role' : filter.label;
      return `${filterLabel}__${_option?.label}`;
    };
    if (!rosterFields) {
      return {};
    }
    const segments = Object.keys(activeFilters).reduce((param, segmentKey) => {
      const filter = rosterFilters[segmentKey];
      const activeKeys = activeFilters[segmentKey];
      activeKeys.forEach((option) => {
        param.push(djangoKey(filter, option));
      });
      return param;
    }, [] as string[]);
    return { segment: segments };
  }, [activeFilters]);

  const clearMsteamsUsers = () => {
    setUsersToAdd([]);
    setUsersWithoutMsteams([]);
  };

  const {
    isFetching: isLoadingMsteamsUsers,
    refetch: refetchMsteamsUsers,
    // removes the msteams data, forcing cache to be invalidated for this specific query
    // needed for adding a new msteams user handle to not just return cached data
    remove: invalidateMsteamsUsers,
  } = useClientQuery(
    RosterClient.getRosterTargets,
    { studyRef, ...activeFiltersQueries },
    {
      enabled: false,
      onSuccess: (rosterUsers) => {
        // reset users and search whenever we update filters and get new users
        if (rosterUsers) {
          let usersWithMsteams = [...rosterUsers].filter((u) => u.msteams);
          let noMsteamsUsers = [...rosterUsers].filter((u) => !u.msteams);
          setUsersToAdd(usersWithMsteams);
          setUsersWithoutMsteams(noMsteamsUsers);
        } else {
          clearMsteamsUsers();
        }
      },
    }
  );

  return (
    <DevExDrawer
      title="Add people from your roster"
      open={props.open}
      onClose={() => props.onClose()}
      width={640}
      closable
      extra={
        <Flex gap={theme.variable.spacing.sm}>
          <DevExButton onClick={props.onClose}>Cancel</DevExButton>
          <DevExButton
            type="primary"
            onClick={() => {
              props.addUsers(usersToAdd);
              props.onClose();
              clearMsteamsUsers();
            }}
            disabled={!usersToAdd.length}
          >
            Add
          </DevExButton>
        </Flex>
      }
    >
      <RosterTargetSegmentCascaderStyles />
      <Flex
        className="roster__filter"
        vertical
        gap={theme.variable.spacing.md}
        css={RosterTargetDrawerStyles}
      >
        <Flex gap={theme.variable.spacing.sm} align="center">
          <Col>
            <DevExText type="secondary" lined>
              Add people from your {}
              {window.dx.user?.canManageRoster ? (
                <>
                  <DevExLink
                    href={`${getDevExEnv().appOrigin}/settings/people/manage`}
                    target="_blank"
                  >
                    Jellyfish roster <FontAwesomeIcon icon={faExternalLink} />
                  </DevExLink>
                  .
                </>
              ) : (
                'Jellyfish roster.'
              )}{' '}
              You can filter their attributes to select the respondents that are relevant to your
              survey.
            </DevExText>
          </Col>
          {!!enabledFilters.length && (
            <DevExButton
              size="small"
              type="text"
              onClick={() => {
                setActiveFilters({});
                setEnabledFilters([]);
                setUsersToAdd([]);
              }}
            >
              Clear filters
            </DevExButton>
          )}
          {rosterSegmentOptions && (
            <Col className="header__dropdown" span={2}>
              <Badge count={enabledFilters.length} color={theme.color.brand.active}>
                <Cascader
                  value={enabledFilters.map((obj) => [obj.value])}
                  multiple
                  options={rosterSegmentOptions}
                  popupClassName="roster-target-segment-cascader"
                  onChange={(keys) => {
                    // check if we unchecked a previously checked filter
                    const removedKeys = enabledFilters
                      .map((f) => f.value)
                      .filter((f) => keys.findIndex((k) => k[0] === f) === -1);

                    // if we have removed it, remove active filters associated with it
                    if (removedKeys.length) {
                      const _activeFilters = { ...activeFilters };
                      removedKeys.forEach((k) => {
                        delete _activeFilters[k];
                      });
                      setActiveFilters(_activeFilters);
                    }
                    const _rosterfilters = { ...rosterFilters };
                    const tmpEnabled: Filter[] = [];
                    keys.forEach((k) => {
                      tmpEnabled.push(_rosterfilters[k[0]]);
                    });

                    setEnabledFilters(tmpEnabled);
                  }}
                >
                  <Tooltip title={'Find MS Teams users based on Jellyfish roster fields.'}>
                    <DevExButton type="outline" icon={<FontAwesomeIcon icon={faFilterList} />} />
                  </Tooltip>
                </Cascader>
              </Badge>
            </Col>
          )}
        </Flex>
        <Row className="filter__content">
          <DevExText>Find people from my Jellyfish Roster</DevExText>
          {!!enabledFilters?.length && <DevExText>where</DevExText>}
          {enabledFilters &&
            enabledFilters.map((f, i) => (
              <Fragment key={f.value}>
                <DevExText>
                  <strong>{f.label}</strong> is one of
                </DevExText>
                <DevExSelect
                  variant="borderless"
                  mode="multiple"
                  maxTagCount={2}
                  style={{ borderBottom: `1px solid ${theme.color.brand.active}` }}
                  options={f.options}
                  placeholder={`any ${f.label.toLowerCase()}`}
                  onChange={(values) => {
                    setActiveFilters({ ...activeFilters, [f.value]: values });
                    trackEvent('survey-editor:msteams-roster-drawer:add-filter', {
                      filterName: f.value,
                      filterValues: values,
                    });
                  }}
                />
                {i !== enabledFilters.length - 1 && <DevExText>AND</DevExText>}
              </Fragment>
            ))}
        </Row>
        <Row className="filter__submit">
          <DevExButton
            icon={<FontAwesomeIcon icon={faMicrosoft} />}
            type="outline"
            onClick={() => {
              trackEvent('survey-editor:msteams-roster-drawer:request-users', {
                filters: enabledFilters,
              });
              refetchMsteamsUsers();
            }}
          >
            Find in MS Teams
          </DevExButton>
        </Row>
        <Divider />
        {/* Valid Users from Request */}
        <MSTeamsTargetList
          users={usersToAddMsteams}
          unlinkedUsers={usersWithoutMsteams}
          onLink={() => {
            invalidateMsteamsUsers();
            refetchMsteamsUsers();
          }}
          onUpdate={(users) => {
            // users is MSTeamsUser, usersToAdd is TargetRespondents.
            // We need to filter users to add by the msteams values here
            const newSet = [...usersToAdd].filter((target) => {
              return !!users.find((user) => user.id === target.msteams?.id);
            });
            setUsersToAdd(newSet);
          }}
          loading={isLoadingMsteamsUsers}
          emptyText="Start adding people from your roster by finding them in MS Teams. You can filter their attributes."
          emptyStateCTA={<>Click &lsquo;Find in MS Teams&rsquo; above to begin.</>}
          trackEventKey="roster-target-drawer"
        />
      </Flex>
    </DevExDrawer>
  );
};
